import React from 'react';
import styled from '@emotion/styled';
import {
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
  transition,
  duration,
  easing,
  layout,
  zIndex,
} from '../../../../style-utils/theme';
import { down, touch } from '../../../../style-utils/media-queries';
import { fadeIn } from '../../../../style-utils/motion';
import { ActiveIndicator, InlineMessage, m } from '../../../../motion';

/**
 * Styled layer for the Units / Occupancy / Roster screens.
 *
 * Every value here resolves to a Deep Iris token — see design_system.md. The
 * only literals are structural (1px hairlines, the 6px pill dot, percentage
 * widths), never colour, radius or elevation.
 */

/* ─────────────────────────  Status pills  ───────────────────────── */

// The five tints from `status.*`. Named after the SIGNAL, not the colour, so a
// palette change never leaves a component asking for "the green one".
export type PillTone = keyof typeof status;

// Block-body prop readers: kept out of the styled template so prettier cannot
// wrap a long member expression inside it and fight the lint rule.
const pillBg = (p: { tone: PillTone }) => {
  return status[p.tone].bg;
};
const pillFg = (p: { tone: PillTone }) => {
  return status[p.tone].fg;
};
const pillDot = (p: { tone: PillTone }) => {
  return status[p.tone].dot;
};

export const Pill = styled.span<{ tone: PillTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  background: ${pillBg};
  color: ${pillFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${radius.circle};
    background: ${pillDot};
    flex: none;
  }
`;

// Same tints without the leading dot — for inline annotations where a dot would
// read as a second, competing status.
/* `$wrap` IS FOR A PILL THAT CARRIES A SENTENCE RATHER THAN A LABEL.
   `white-space: nowrap` is right for "Primary", "No login", "3 people" — a pill
   broken across two lines stops reading as one token. It is wrong for the
   roster's "Never signed out · open since yesterday", which is 242px of text in
   a flex row starting 132px in: at 320px that pushed the DOCUMENT 55px wide and
   the whole page scrolled sideways, which the brief forbids outright. Measured
   by scripts/analytics/responsive.js on /dashboard/roster — a route nothing in
   this repo had ever loaded until WP-38 added it, and only when the fixture has
   a stale visitor in it, which is why five sessions never saw it.
   `min-width: 0` as well: without it the flex item refuses to shrink below its
   content even once wrapping is allowed. */
export const Tag = styled.span<{ tone: PillTone; $wrap?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${pillBg};
  color: ${pillFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  white-space: ${({ $wrap }) => ($wrap ? 'normal' : 'nowrap')};
  ${({ $wrap }) => ($wrap ? 'min-width: 0; overflow-wrap: anywhere;' : '')}
`;

/* ─────────────────────────────  Shell  ──────────────────────────── */

/* No entrance of its own: the dashboard's ContentTransition brings the screen
   in, and a second fade on top of it read as the page loading twice. */
export const Wrap = styled.div`
  padding: ${spacing.lg};

  ${down('md')} {
    padding: ${layout.gutterMobile};
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
  flex-wrap: wrap;
`;

export const HeaderText = styled.div`
  min-width: 0;
`;

export const Title = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const Sub = styled.p`
  margin: ${spacing.half} 0 0;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

/* The units list's header: its buttons line up by their tops, so "+ Add unit"
   stays level with "Who's Home" while its disabled reason hangs below it
   (centring lifted the button by half the reason's height). */
export const HeaderButtons = styled(HeaderActions)`
  align-items: flex-start;
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  margin-bottom: ${spacing.sm};
  padding: 0;
  border: none;
  background: transparent;
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.lagoon};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

/* ───────────────────────────  Buttons  ──────────────────────────── */

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.half};
  min-height: ${layout.controlHeight};
  padding: ${spacing.xs} ${spacing.md};
  border: none;
  border-radius: ${radius.pill};
  background: ${palette.lagoon};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.base};

  &:hover:not(:disabled) {
    background: ${palette.irisDark};
    box-shadow: ${shadow.brand};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.half};
  min-height: ${layout.controlHeight};
  padding: ${spacing.xs} ${spacing.md};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

// Destructive actions stay visually quiet until hover: a table full of red
// buttons reads as a page full of errors.
export const DangerButton = styled(GhostButton)`
  /* Coral text at rest so the control reads as destructive BEFORE it is hovered
     — a danger button that looks ordinary until the pointer is already on it
     warns nobody. status.alert.fg rather than palette.alert: this is text, and
     #F0564A on the soft tint measures 1.9:1. */
  color: ${status.alert.fg};
  border-color: ${palette.lineStrong};

  &:hover:not(:disabled) {
    border-color: ${palette.alertStrong};
    color: ${palette.white};
    background: ${palette.alertStrong};
  }
`;

// Row-level actions — smaller than a page-level control, still above the 44px
// touch target on the card reflow where they become the primary affordance.
export const RowButton = styled.button`
  padding: ${spacing.half} ${spacing.sm};
  /* A 25px ROW ACTION IS NOT A TOUCH TARGET. Measured at 25px on Units on every
     touch shape — below compactControlHeight (32px), let alone the 44px
     touchTarget the design system requires. These are Edit and Delete: the
     two controls on the row where a mis-tap costs the most. The height only
     grows where there is a finger, so the dense desk layout is unchanged. */
  ${touch} {
    min-height: ${layout.touchTarget};
  }
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  /* :not(:disabled) — WP-29 gives these row controls a disabled state, and a
     button that still lights up under the pointer reads as pressable. Same
     omission WP-40 found on the IQ composer: the attribute was honoured, the
     styling never was. */
  &:not(:disabled):hover {
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }

  /* Matches the two buttons above in this file rather than inventing a third
     disabled treatment. */
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const RowDangerButton = styled(RowButton)`
  &:not(:disabled):hover {
    border-color: ${palette.alert};
    color: ${status.alert.fg};
    background: ${palette.alertSoft};
  }
`;

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.half};

  ${down('md')} {
    justify-content: flex-start;
  }
`;

/* ───────────────────────────  Toolbar  ──────────────────────────── */

export const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
  flex-wrap: wrap;
`;

export const ToolbarField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 260px;
  flex: 1 1 260px;
  max-width: 420px;
`;

export const ToolbarSpacer = styled.div`
  flex: 1 1 auto;
`;

const filterBg = (p: { active?: boolean }) => {
  return p.active ? palette.irisTint : palette.surface;
};
const filterFg = (p: { active?: boolean }) => {
  return p.active ? palette.irisDark : palette.slate;
};
const filterBorder = (p: { active?: boolean }) => {
  return p.active ? palette.iris : palette.lineStrong;
};

// A filter is a state, not a command, so it reads as a toggled pill rather than
// a button — pressed-ness is carried by aria-pressed for screen readers.
export const FilterToggle = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.controlHeight};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${filterBorder};
  border-radius: ${radius.pill};
  background: ${filterBg};
  color: ${filterFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ResultCount = styled.span`
  font-size: ${typography.size.xs}px;
  color: ${palette.slateLight};
  white-space: nowrap;
`;

/* ────────────────────────  Form primitives  ─────────────────────── */

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

const controlBase = `
  width: 100%;
  min-height: ${layout.fieldHeight};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  outline: none;
  transition: ${transition.colors};

  /* 16px ON A FINGER, AND IT IS NOT A TYPE CHOICE.
     iOS zooms the whole page when a text control under 16px takes focus, and it
     does not zoom back out — so a 14px field on this screen throws the reader
     into a horizontally-scrolled layout they then have to pinch out of.
     Measured: scripts/analytics/responsive.js reported select[select-one] at
     14px on Units on every touch shape. Same value dashboard-users.styled.tsx
     already uses for the same reason. */
  ${touch} {
    font-size: 16px;
  }

  &::placeholder {
    color: ${palette.slateLight};
  }

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
  }

  &:focus {
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
    cursor: default;
  }
`;

const invalidRing = (p: { invalid?: boolean }) => {
  return p.invalid ? palette.alert : palette.lineStrong;
};

export const Input = styled.input<{ invalid?: boolean }>`
  ${controlBase}
  border-color: ${invalidRing};

  /* The error ring only when the field IS invalid. This used to be
     shadow.focusError unconditionally, so every valid unit-form field lit
     up red the moment it took focus. */
  &:focus {
    border-color: ${(p) => (p.invalid ? palette.alert : palette.lagoon)};
    box-shadow: ${(p) => (p.invalid ? shadow.focusError : shadow.focus)};
  }
`;

export const Select = styled.select`
  ${controlBase}
  cursor: pointer;
`;

export const Textarea = styled.textarea`
  ${controlBase}
  min-height: 96px;
  padding: ${spacing.sm};
  resize: vertical;
  line-height: ${typography.leading.normal};
`;

/* A validation line that UNFOLDS under its field (InlineMessage: the height
   animates open, so the control below slides rather than jumps) and folds away
   when the message clears. The slot is display:none while empty so it takes no
   share of the Field's column gap. */
const FieldErrorSlot = styled.div`
  &:empty {
    display: none;
  }

  & [role='alert'] {
    margin: 0;
    font-size: ${typography.size.xs}px;
    font-weight: ${typography.weight.medium};
    color: ${status.alert.fg};
  }
`;

export const FieldError: React.FC<{ id?: string; message?: string }> = ({
  id,
  message,
}) => (
  <FieldErrorSlot>
    <InlineMessage id={id} message={message || undefined} />
  </FieldErrorSlot>
);

export const Hint = styled.p`
  margin: 0;
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slateLight};
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.sm};

  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

export const FormRowThree = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing.sm};

  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

export const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }

  input {
    margin-top: ${spacing.half};
    accent-color: ${palette.lagoon};
    width: 18px;
    height: 18px;
    flex: none;
    cursor: pointer;
  }
  /* The input inside is what takes focus; without this the row it lives in gave
     a mouse a hover state and a keyboard nothing at all. */
  &:focus-within {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const CheckText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

/* ───────────────────  Segmented control (mode switch)  ──────────── */

export const Segmented = styled.div`
  display: inline-flex;
  gap: ${spacing.half};
  padding: ${spacing.half};
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
`;

const segFg = (p: { active?: boolean }) => {
  return p.active ? palette.white : palette.slate;
};

/* The selected fill is SegmentedIndicator below — one pill that slides to the
   pressed segment — so the tab itself stays transparent and only its label
   colour changes. */
export const SegmentedTab = styled.button<{ active?: boolean }>`
  position: relative;
  isolation: isolate;
  padding: ${spacing.xs} ${spacing.md};
  border: none;
  border-radius: ${radius.pill};
  background: transparent;
  color: ${segFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* 44px ON A TOUCH DEVICE (R-18). The padding alone gave it 33px, measured by
     the device sweep at 320, 390 and 768 — a real miss on a control a concierge
     switches while holding a parcel. Touch only, so a desk mouse keeps the
     compact pill.

     NO BACKTICKS AND NO DOLLAR-BRACE IN THIS COMMENT - it is inside a styled
     template literal, so both are parsed as interpolation. The first version
     wrote the media-query helper's name in backticks and the build failed with
     "Variable 'touch' implicitly has an 'any' type" pointing at the COMMENT. */
  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const SegmentedLabel = styled.span`
  position: relative;
  z-index: 1;
`;

/* lagoon under a white 14px label: white on #00A19A is 3.20:1, under the
   4.5:1 that size needs. Same swap as every other selected pill in the app. */
export const SegmentedIndicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: ${radius.pill};
  background: ${palette.lagoon};
  pointer-events: none;
`;

/* ────────────────────────────  Table  ───────────────────────────── */

export const TableWrap = styled.div`
  border-radius: ${radius.md};

  ${down('md')} {
    background: transparent;
    border: none;
    box-shadow: none;
  }
`;

const TableBase = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  overflow: hidden;

  ${down('md')} {
    display: block;
    background: transparent;
    border: none;
    box-shadow: none;
    overflow: visible;
  }
`;

const ThBase = styled.th`
  text-align: left;
  padding: ${spacing.sm};
  background: ${palette.surface};
  border-bottom: 1px solid ${palette.line};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  white-space: nowrap;
`;

/* Extends the styled BASE, not the wrapper below it — the wrapper is declared
   later in the file and only adds the default scope. ThRight gets that same
   default by being wrapped itself, just below. */
const ThRightBase = styled(ThBase)`
  text-align: right;
`;

// Rows lift on hover. `clickable` is a separate prop from the handler so a row
// without a detail view never advertises one with a pointer cursor.
//
// A MOTION ROW: the site spreads listItemMotion(index) on it, which is the
// capped cascade (STAGGER.maxItems) via variants rather than an animation-delay
// per row. `layout` is passed as false at every site — this list is unbounded
// (a tower is 300 doors) and a layout animation across it would cost more than
// it shows. `index` is accepted for callers that still pass it and is unused.
const TrBase = m.tr<{ clickable?: boolean; index?: number }>`
  cursor: ${p => (p.clickable ? 'pointer' : 'default')};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  &:last-of-type td {
    border-bottom: none;
  }

  ${down('md')} {
    display: block;
    margin-bottom: ${spacing.sm};
    padding: ${spacing.xs};
    border: 1px solid ${palette.line};
    border-radius: ${radius.md};
    background: ${palette.surface};
    box-shadow: ${shadow.sm};

    /* Shadow only: the motion layer owns this row's transform. */
    &:hover {
      box-shadow: ${shadow.md};
    }

    &:last-of-type td {
      border-bottom: none;
    }
  }
`;

export const Td = styled.td`
  padding: ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
  color: ${palette.ink};
  font-size: ${typography.size.sm}px;
  vertical-align: middle;

  ${down('md')} {
    display: flex;
    /* A value too wide to sit beside its label drops under it instead of
       pushing the page sideways: "Occupied · 2 occupants" is a one-line pill
       and, next to OCCUPANCY at 320, overran the viewport by 7px (device
       sweep, building with two occupants). */
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.sm};
    padding: ${spacing.xs};
    border-bottom: 1px solid ${palette.line};

    > * {
      max-width: 100%;
    }

    /* The header row is gone at this width, so each cell carries its own
       label — set as data-label on the <td>. */
    &::before {
      content: attr(data-label);
      flex: none;
      color: ${palette.slateLight};
      font-size: ${typography.size.xxs}px;
      font-weight: ${typography.weight.semibold};
      letter-spacing: ${typography.tracking.caps};
      text-transform: uppercase;
    }

    &:empty {
      display: none;
    }
  }
`;

export const TdRight = styled(Td)`
  text-align: right;
`;

// The identity column: heavier than its neighbours so a scan down the table
// lands on the unit number first.
export const UnitNumber = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
  /* Both of these carry text a customer typed, so both need the pair. See the
     note on UnitName below for why break-word is not enough. */
  min-width: 0;
  overflow-wrap: anywhere;
`;

// The optional custom label (033), shown under the number in the list.
export const UnitName = styled.span`
  display: block;
  margin-top: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  letter-spacing: ${typography.tracking.snug};
  /* THE DOOR'S HUMAN NAME, so whatever the building calls it. Measured at 320px
     with a 66-character label: 421px wide inside a 238px cell, taking the page
     to 748px. As a block inside a flex row its automatic min-width floor is its
     min-content, and the global break-word does not lower min-content — only
     anywhere does. NO BACKTICKS IN THIS COMMENT: styled template literal. */
  min-width: 0;
  overflow-wrap: anywhere;
`;

// Same slot and the same metrics as UnitName, so a named row and an unnamed
// row are exactly the same height and a 150-row column never goes ragged.
// slateLight is the documented placeholder token (design_system.md:67) and is
// already what Muted resolves to, so no new hex enters a call site.
export const UnitNameEmpty = styled(UnitName)`
  color: ${palette.slateLight};
  font-style: italic;
  /* A fixed two-word label, never a long name: it must not inherit the
     break-anywhere above, which let the building modal squeeze its unit
     column to 33px and stack this label over three lines (Stays journey run
     s10191106c8e, J-S1.6). */
  overflow-wrap: normal;
  white-space: nowrap;
`;

export const Muted = styled.span`
  color: ${palette.slateLight};
`;

export const Thead = styled.thead`
  ${down('md')} {
    /* CLIPPED, NOT display:none (WP-37 / MR-15).
       This said "labels move onto the cells themselves below md" — and they do,
       as CSS generated content, which is NOT IN THE DOM and which VoiceOver
       ignores. Meanwhile display:none removed the real header cells from the
       accessibility tree, so the role="table" declared a few hundred lines below
       promised header association and then had none: a table whose every cell is
       unnamed, which is worse than a plain list.

       Clipping keeps the native th-to-td association and costs no layout. The
       generated content stays as the VISUAL label on the card.
       NO BACKTICKS IN THIS COMMENT - styled template literal. */
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

const TbodyBase = styled.tbody`
  ${down('md')} {
    display: block;
  }
`;

/* ────────────────────────────  Cards  ───────────────────────────── */

export const Card = styled.div`
  padding: ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${down('md')} {
    padding: ${spacing.sm};
  }
`;

export const CardTitle = styled.h3`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  flex-wrap: wrap;
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${spacing.md};
  align-items: start;

  ${down('lg')} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

/* ──────────────────────  Unit detail summary  ───────────────────── */

export const SpecGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${spacing.sm};
  margin: 0;
`;

export const Spec = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
`;

export const SpecLabel = styled.dt`
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slateLight};
`;

export const SpecValue = styled.dd`
  margin: 0;
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.ink};
`;

export const NoteBlock = styled.p`
  margin: ${spacing.sm} 0 0;
  padding: ${spacing.sm};
  border-left: 2px solid ${palette.irisTint};
  border-radius: ${radius.xs};
  background: ${palette.irisTintSoft};
  color: ${palette.inkSoft};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  white-space: pre-wrap;
`;

/* ────────────────────  Occupant list (current)  ─────────────────── */

export const OccupantRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  transition: ${transition.base};

  & + & {
    margin-top: ${spacing.xs};
  }

  &:hover {
    border-color: ${palette.lineStrong};
    background: ${palette.irisTintSoft};
  }

  ${down('sm')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const OccupantIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const OccupantName = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const OccupantMeta = styled.span`
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

/* ────────────────────────  Occupancy timeline  ──────────────────── */

export const Timeline = styled.ol`
  position: relative;
  margin: 0;
  padding: 0 0 0 ${spacing.md};
  list-style: none;

  /* The spine. Sits under the markers, which paint their own surface ring to
     cut it cleanly at each node. */
  &::before {
    content: '';
    position: absolute;
    top: ${spacing.xs};
    bottom: ${spacing.xs};
    left: 5px;
    width: 2px;
    border-radius: ${radius.pill};
    background: ${palette.line};
  }
`;

/* Arrives via listItemMotion(index) spread at the site: the same capped
   cascade every list in the product uses. */
export const TimelineItem = m.li`
  position: relative;
  padding: 0 0 ${spacing.md} ${spacing.sm};

  &:last-of-type {
    padding-bottom: 0;
  }
`;

const markerBg = (p: { open?: boolean }) => {
  return p.open ? palette.iris : palette.surface;
};
const markerBorder = (p: { open?: boolean }) => {
  return p.open ? palette.iris : palette.lineStrong;
};

// Open occupancies are a filled iris node, closed ones a hollow slate node —
// the single glance that answers "is this person still here".
export const TimelineMarker = styled.span<{ open?: boolean }>`
  position: absolute;
  top: ${spacing.half};
  left: calc(-1 * ${spacing.md} + 1px);
  width: 12px;
  height: 12px;
  border: 2px solid ${markerBorder};
  border-radius: ${radius.circle};
  background: ${markerBg};
  box-shadow: 0 0 0 4px ${palette.surface};
`;

const entryBg = (p: { open?: boolean }) => {
  return p.open ? palette.irisTintSoft : palette.surface;
};
const entryBorder = (p: { open?: boolean }) => {
  return p.open ? palette.irisTint : palette.line;
};

export const TimelineEntry = styled.div<{ open?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${entryBorder};
  border-radius: ${radius.sm};
  background: ${entryBg};
  transition: ${transition.base};

  &:hover {
    box-shadow: ${shadow.sm};
  }

  ${down('sm')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const TimelineBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const TimelineName = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const TimelineDates = styled.span`
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
  font-variant-numeric: tabular-nums;
`;

export const TimelineReason = styled.span`
  font-size: ${typography.size.xs}px;
  font-style: italic;
  color: ${palette.slateLight};
`;

/* ──────────────────────────  Roster  ────────────────────────────── */

/* WP-32 — Who's Home is TWO LABELLED SECTIONS.
 *
 * Residents (people who LIVE here) and visitors inside (people who WALKED IN
 * and were never signed out) are different kinds of fact. The section furniture
 * below exists so neither can be read as the other: each gets its own heading,
 * its own count and its own empty state, and there is deliberately no styling
 * anywhere here that would let the two lists run together. */

export const RosterSection = styled.section`
  & + & {
    margin-top: ${spacing.xl};
    padding-top: ${spacing.lg};
    border-top: 1px solid ${palette.line};
  }
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

// The section's OWN count. Never a total across sections — a sum of residents
// and visitors is a number that answers no question.
export const SectionCount = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
  white-space: nowrap;
`;

export const SectionNote = styled.p`
  margin: 0 0 ${spacing.sm};
  max-width: 68ch;
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const RosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${spacing.sm};
`;

/* A motion card: the site spreads listItemMotion(index) — capped cascade via
   variants. The hover lift is shadow-only because the motion layer owns the
   card's transform. */
export const RosterCard = m.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  &:hover {
    box-shadow: ${shadow.md};
  }

  ${down('md')} {
    padding: ${spacing.sm};
  }
`;

// The unit number is the front desk's primary key — a resident is looked up BY
// door, so it gets the largest, most contrasted block on the card.
export const RosterUnit = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  min-width: 64px;
  padding: ${spacing.xs};
  border-radius: ${radius.sm};
  background: ${palette.irisTint};
  color: ${palette.irisDeep};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.tight};
  line-height: ${typography.leading.tight};
  text-align: center;
  word-break: break-word;
`;

export const RosterBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const RosterName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const RosterMeta = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex-wrap: wrap;
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

/* WP-32 — the visitor half. Block-body prop readers, kept out of the styled
 * templates for the same reason as the pill ones above. */
const visitorBorder = (p: { stale?: boolean }) => {
  return p.stale ? status.warning.dot : palette.line;
};
const visitorWhenBg = (p: { stale?: boolean }) => {
  return p.stale ? status.warning.bg : status.neutral.bg;
};
const visitorWhenFg = (p: { stale?: boolean }) => {
  return p.stale ? status.warning.fg : status.neutral.fg;
};

// An open arrival from a PREVIOUS day is not the same claim as one from this
// shift, so it does not get the same card. The difference is a border and a
// tint, never a hidden row: the ghosts are exactly what the desk has to clear.
export const VisitorCard = styled(RosterCard)<{ stale?: boolean }>`
  border-color: ${visitorBorder};
`;

// A visitor is looked up BY WHEN THEY CAME IN, so the arrival time takes the
// slot the unit number takes on a resident card.
export const VisitorWhen = styled.div<{ stale?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: none;
  min-width: 64px;
  padding: ${spacing.xs};
  border-radius: ${radius.sm};
  background: ${visitorWhenBg};
  color: ${visitorWhenFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.tight};
  line-height: ${typography.leading.tight};
  text-align: center;
`;

export const VisitorWhenDay = styled.span`
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  text-transform: uppercase;
`;

/* ───────────────────────  States & feedback  ────────────────────── */

export const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xxl};
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xl};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
  background: ${palette.surface};
  text-align: center;
`;

export const EmptyTitle = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const EmptyText = styled.p`
  margin: 0;
  max-width: 46ch;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const bannerBg = (p: { tone?: PillTone }) => {
  return status[p.tone ?? 'alert'].bg;
};
const bannerFg = (p: { tone?: PillTone }) => {
  return status[p.tone ?? 'alert'].fg;
};

export const Banner = styled.div<{ tone?: PillTone }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${bannerBg};
  color: ${bannerFg};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  animation: ${fadeIn} ${duration.fast} ${easing.standard} both;
`;

export const BannerActions = styled.div`
  display: flex;
  gap: ${spacing.xs};
  margin-top: ${spacing.xs};
  flex-wrap: wrap;
`;

/* ──────────────────────────  Modal  ─────────────────────────────── */

/* Motion elements: scrimMotion / useOverlayMotion('modal') are spread on them
   at the site, and inside a Presence they animate OUT as well. */
export const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.sm};
  background: ${palette.overlay};
  backdrop-filter: blur(8px);
  overflow-y: auto;

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

export const Modal = m.div`
  width: 100%;
  max-width: 560px;
  max-height: 92vh;
  max-height: 92dvh;
  overflow-y: auto;
  padding: ${spacing.md};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.lg};

  ${down('sm')} {
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    padding: ${spacing.sm};
    border-radius: ${radius.lg} ${radius.lg} 0 0;
  }
`;

export const ModalHeader = styled.div`
  margin-bottom: ${spacing.md};
`;

export const ModalTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const ModalSub = styled.p`
  margin: ${spacing.half} 0 0;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.xs};
  margin-top: ${spacing.md};
  flex-wrap: wrap;
`;

/* A text-weight action that sits inside a sentence — used beside the roster's
   "Read at HH:MM" so the reader can make that time current. Not a filled button:
   it is a correction to a caption, not the panel's primary action. */
export const InlineRefreshButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: ${palette.lagoon};
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }

  ${touch} {
    min-height: ${layout.touchTarget};
    padding: 0 ${spacing.half};
  }
`;

/* Matches Select in the same toolbar so the two controls read as one row. */
export const UnitSearchInput = styled.input`
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;

  &::placeholder {
    color: ${palette.slateLight};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* ── ROLES THAT SURVIVE THE REFLOW ──────────────────────────────────────────
   Below md these elements are `display: block`, which strips the implicit ARIA
   roles a table carries. Declaring them explicitly is what keeps the reflowed
   card list a TABLE for assistive tech — same rows, same headers, same
   associations, whatever the CSS is doing to the boxes. */

export const Table: React.FC<React.ComponentProps<typeof TableBase>> = ({
  role = 'table',
  ...rest
}) => <TableBase role={role} {...rest} />;

/* WP-37 — scope="col" BY DEFAULT, the same rule the shared engine applies.
   A header that does not say what it heads is the defect this consolidation
   exists to end, and the live accessibility tree said this table had seven
   header cells and zero of them scoped. */
export const Th: React.FC<React.ComponentProps<typeof ThBase>> = ({
  scope = 'col',
  ...rest
}) => <ThBase scope={scope} {...rest} />;

export const ThRight: React.FC<React.ComponentProps<typeof ThRightBase>> = ({
  scope = 'col',
  ...rest
}) => <ThRightBase scope={scope} {...rest} />;

export const Tbody: React.FC<React.ComponentProps<typeof TbodyBase>> = ({
  role = 'rowgroup',
  ...rest
}) => <TbodyBase role={role} {...rest} />;

export const Tr: React.FC<React.ComponentProps<typeof TrBase>> = ({
  role = 'row',
  ...rest
}) => <TrBase role={role} {...rest} />;
